import React, { Key, ReactElement } from 'react';
import clsx from 'clsx';
import { Alignment, MultipleSelection } from '@react-types/shared';
import {
  Menu as AriaMenu,
  MenuProps as AriaMenuProps,
  MenuTrigger as AriaMenuTrigger,
} from 'react-aria-components';
import { MenuTriggerProps } from '@react-types/menu';
import { AriaMenuTriggerProps } from 'react-aria/useMenu';
import { AriaPositionProps } from 'react-aria/useOverlayPosition';

import styles from '@danske/sapphire-css/components/listbox/listbox.module.css';

import { ButtonProps } from '../../Button';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { BaseComponentKeys, GlobalDomAttributes } from '../../utils/types';
import { Popover } from '../../Popover';
import {
  useV5Compatibility,
  V5MenuCompatibilityContext,
} from './v5Compatibility';

export type MenuProps<T extends object> = Pick<
  AriaMenuProps<T>,
  | 'children'
  | BaseComponentKeys
  | 'autoFocus'
  | 'onAction'
  | 'items'
  | 'onClose'
> &
  MenuTriggerProps &
  Pick<AriaMenuTriggerProps, 'isDisabled'> &
  SapphireStyleProps &
  GlobalDomAttributes & {
    triggerElement: ReactElement<ButtonProps<'button'>>;
    // TODO(v6): remove
    /**
     * @deprecated use {@link MenuItemProps.isDangerous isDangerous} prop on MenuItem instead.
     */
    dangerKeys?: Iterable<Key>;

    /**
     * ids of the disabled menu items.
     * @deprecated use `isDisabled` prop on `MenuItem` instead.
     */
    disabledKeys?: MultipleSelection['disabledKeys'];
    /**
     * Alignment of the menu relative to the trigger.
     * @default 'start'
     */
    align?: Alignment;
    /**
     * Where the Menu opens relative to its trigger.
     * @default 'bottom'
     */
    direction?: 'bottom' | 'top' | 'left' | 'right' | 'start' | 'end';
    /**
     * Whether the menu should automatically flip direction when space is
     * limited.
     * @default true
     */
    shouldFlip?: boolean;
    /**
     * Whether the Menu closes when a selection is made.
     * @default true
     */
    closeOnSelect?: boolean;
  };

/**
 * A menu displays a list of actions or options that a user can choose.
 */
export function Menu<T extends object>({
  children,
  triggerElement,
  direction = 'bottom',
  align = 'start',
  shouldFlip = true,
  dangerKeys,
  ...props
}: MenuProps<T>): React.JSX.Element {
  const { styleProps } = useSapphireStyleProps(props);
  return (
    <AriaMenuTrigger {...props}>
      {triggerElement}
      <Popover
        placement={placementValue(direction, align)}
        shouldFlip={shouldFlip}
        defaultMaxWidth
      >
        <V5MenuCompatibilityContext.Provider value={{ dangerKeys }}>
          <AriaMenu
            {...props}
            className={clsx(styles['sapphire-listbox'], styleProps.className)}
            style={styleProps.style}
          >
            {useV5Compatibility(children)}
          </AriaMenu>
        </V5MenuCompatibilityContext.Provider>
      </Popover>
    </AriaMenuTrigger>
  );
}

const placementValue = (
  direction: MenuProps<object>['direction'],
  align: MenuProps<object>['align']
): AriaPositionProps['placement'] => {
  switch (direction) {
    case 'left':
    case 'right':
    case 'start':
    case 'end':
      return `${direction} ${
        align === 'end' ? ('bottom' as const) : ('top' as const)
      }` as AriaPositionProps['placement'];
    case 'bottom':
    case 'top':
    default:
      return `${direction} ${align}` as AriaPositionProps['placement'];
  }
};
